import { faDownload, faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import { IconTrashCan } from 'components/icons/IconTrashCan'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'

const statusLabels: Record<SharedTypes.TOnDemandReportStatus, () => string> = {
  pending: () => translate.phrases.banyanApp('Pending'),
  completed: () => translate.phrases.banyanApp('Completed'),
  failed: () => translate.phrases.banyanApp('Failed'),
}

const headerCellCss = {
  fontWeight: 400,
  fontSize: 14,
  color: colors.midnight,
  textAlign: 'left' as const,
  padding: '10px 10px 10px 0',
}

const bodyCellCss = {
  fontSize: 14,
  color: colors.midnight,
  padding: '20px 10px 20px 0',
  verticalAlign: 'top' as const,
}

const dividerCss = {
  borderLeft: `1px solid ${colors.grey200}`,
  paddingLeft: 10,
}

const formatDate = (yyyymmdd: string, timezone: string) =>
  moment.tz(yyyymmdd, 'YYYY-MM-DD', true, timezone).format('MMM D, YYYY')

export const IrrigationReportsTable = ({
  onDelete,
  onDownload,
  onRetry,
  reports,
}: {
  onDelete: (reportId: string) => Promise<void>
  onDownload: (reportId: string) => Promise<void>
  onRetry: (reportId: string) => Promise<void>
  reports: SharedTypes.TOnDemandIrrigationReportWithProperty[]
}) => {
  const browserTimezone = moment.tz.guess()

  return (
    <table css={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr css={{ borderBottom: `1px solid ${colors.midnight}` }}>
          <th css={headerCellCss}>{translate.phrases.banyanApp('Property')}</th>
          <th css={[headerCellCss, dividerCss]}>{translate.phrases.banyanApp('Reporting Period')}</th>
          <th css={[headerCellCss, dividerCss]}>{translate.phrases.banyanApp('Recipients')}</th>
          <th css={[headerCellCss, dividerCss]}>{translate.phrases.banyanApp('Status')}</th>
          <th css={[headerCellCss, dividerCss]}>{translate.phrases.banyanApp('Created On')}</th>
          <th css={[headerCellCss, dividerCss]}>{translate.phrases.banyanApp('Report Link')}</th>
        </tr>
      </thead>
      <tbody>
        {reports.map((report) => (
          <tr key={report.id} css={{ borderBottom: `1px solid ${colors.grey200}` }}>
            <td css={[bodyCellCss, { fontWeight: 500 }]}>{report.propertyName}</td>
            <td css={[bodyCellCss, dividerCss, { fontWeight: 500 }]}>
              {`${formatDate(report.dateFrom, browserTimezone)} - ${formatDate(
                report.dateTo,
                browserTimezone,
              )}`}
            </td>
            <td css={[bodyCellCss, dividerCss, { fontWeight: 600, wordBreak: 'break-word' }]}>
              {report.recipients.length > 0 ? report.recipients.join(', ') : '-'}
            </td>
            <td css={[bodyCellCss, dividerCss, { fontWeight: 600 }]}>{statusLabels[report.status]()}</td>
            <td css={[bodyCellCss, dividerCss, { fontWeight: 600 }]}>
              {moment.tz(report.createdAt, browserTimezone).format('MMM D, YYYY')}
            </td>
            <td css={[bodyCellCss, dividerCss, { whiteSpace: 'nowrap' }]}>
              {report.status === 'completed' && (
                <button
                  type="button"
                  aria-label={translate.phrases.banyanApp('Report Link')}
                  onClick={() => {
                    onDownload(report.id)
                  }}
                  css={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  <FontAwesomeIcon
                    css={{
                      'transition': '200ms ease all',
                      'color': colors.primary,
                      ':hover': { color: colors.primaryLight },
                    }}
                    icon={faDownload}
                    size="lg"
                  />
                </button>
              )}
              {report.status === 'failed' && (
                <button
                  type="button"
                  aria-label={translate.phrases.banyanApp('Retry')}
                  onClick={() => {
                    onRetry(report.id)
                  }}
                  css={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  <FontAwesomeIcon
                    css={{
                      'transition': '200ms ease all',
                      'color': colors.primary,
                      ':hover': { color: colors.primaryLight },
                    }}
                    icon={faRotateRight}
                    size="lg"
                  />
                </button>
              )}
              {report.status !== 'pending' && (
                <button
                  type="button"
                  aria-label={translate.phrases.banyanApp('Delete')}
                  onClick={() => {
                    onDelete(report.id)
                  }}
                  css={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    marginLeft: 14,
                  }}
                >
                  <IconTrashCan />
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
